import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const RequestConfirmation = () => {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
      const response = await api.get(`/api/requests/${id}/public`);
      setRequest(response.data.data);
        setError('');
      } catch (error) {
        console.error('Error fetching request:', error);
        setError('Impossible de charger les détails de la demande. Veuillez vérifier votre identifiant de demande.');
        toast.error('Erreur lors du chargement des détails de la demande');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRequest();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
        <div className="max-w-md w-full glassmorphism p-8 rounded-xl text-center">
          <img
            className="mx-auto h-16 w-auto mb-6"
            src="/assets/logo.webp"
            alt="Tchin-Lait"
          />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Chargement...
          </h2>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
        <div className="max-w-md w-full glassmorphism p-8 rounded-xl text-center">
          <img
            className="mx-auto h-16 w-auto mb-6"
            src="/assets/logo.webp"
            alt="Tchin-Lait"
          />
          <svg
            className="mx-auto h-12 w-12 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mt-4 mb-2">
            Erreur
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/request"
            className="btn-primary inline-block"
          >
            Retour au formulaire de demande
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
      <div className="max-w-lg w-full glassmorphism p-8 rounded-xl">
        <div className="text-center mb-8">
          <img
            className="mx-auto h-16 w-auto mb-6"
            src="/assets/logo.webp"
            alt="Tchin-Lait"
          />
          <svg
            className="mx-auto h-12 w-12 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
            Demande soumise avec succès
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Votre demande a été enregistrée et sera traitée dans les plus brefs délais.
          </p>
        </div>

        <div className="bg-white bg-opacity-50 p-6 rounded-lg shadow-sm mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Détails de la demande</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Numéro de demande</p>
              <p className="text-base font-medium text-gray-900">
                {request?._id ? 
                  // Formatage de l'ID pour qu'il soit plus court et lisible
                  `REQ-${request._id.toString().substring(0, 8).toUpperCase()}` 
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Date de soumission</p>
              <p className="text-base font-medium text-gray-900">
                {request?.createdAt ? new Date(request.createdAt).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Statut</p>
              <p className="text-base font-medium text-gray-900">En attente de validation</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Département cible</p>
              <p className="text-base font-medium text-gray-900">
                {request?.currentStage === 'marketing' ? 'Marketing' : 
                 request?.currentStage === 'force_de_vente' ? 'Force de vente' : 
                 request?.currentStage === 'directrice' ? 'Direction Marketing' :
                 request?.currentStage === 'commercial' ? 'Commercial' : '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white bg-opacity-50 p-6 rounded-lg shadow-sm mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Prochaines étapes</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Votre demande sera examinée par le département concerné.</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Vous recevrez un email de confirmation à chaque étape du processus de validation.</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>En cas de refus, vous serez informé par email avec le motif du refus.</span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Conservez votre numéro de demande pour toute référence future.</span>
            </li>
          </ul>
        </div>

        <div className="text-center">
          <Link
            to="/request"
            className="btn-primary inline-block"
          >
            Soumettre une nouvelle demande
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RequestConfirmation;
